import { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { X, Cpu, CheckCircle, Wrench, CalendarDays, UserCircle, MapPin } from "lucide-react";
import { chantiersAPI, noeudsAPI, automatisationsAPI } from "../api/services";
import { formatDateLong } from "../utils/dateUtils";
import { useSocket } from "../context/SocketContext";
import NodeCard from "../components/widgets/NodeCard";
import ManualOverrideModal from "../components/modals/ManualOverrideModal";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Icônes colorées par catégorie
const makeIcon = (color) => L.divIcon({
  html: `<div style="width:14px;height:14px;background:${color};border:2.5px solid white;border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,0.3);"></div>`,
  className: "", iconSize: [14, 14], iconAnchor: [7, 7],
});
const iconCapteur = makeIcon("#3b82f6");
const iconActionneur = makeIcon("#22c55e");
const iconPasserelle = makeIcon("#f59e0b");

function FitBounds({ bounds }) {
  const map = useMap();
  useEffect(() => {
    if (bounds && bounds.isValid()) map.fitBounds(bounds, { padding: [40, 40], maxZoom: 18 });
  }, [bounds, map]);
  return null;
}

function ChantierMap({ chantier, noeuds }) {
  // Fusionner les coordonnées du chantier.noeuds avec les données live (noeuds)
  const geoNodes = useMemo(() => {
    if (!chantier?.noeuds) return [];
    return chantier.noeuds
      .filter(cn => cn.latitude && cn.longitude)
      .map(cn => {
        const live = noeuds.find(n => n.sn === cn.sn);
        return {
          sn: cn.sn,
          type: cn.type || live?.type || "",
          categorie: live?.categorie || "Capteur",
          etat: live?.etat || "Hors ligne",
          lat: cn.latitude,
          lng: cn.longitude,
        };
      });
  }, [chantier?.noeuds, noeuds]);

  const bounds = useMemo(() => {
    if (geoNodes.length === 0) return null;
    return L.latLngBounds(geoNodes.map(n => [n.lat, n.lng]));
  }, [geoNodes]);

  if (geoNodes.length === 0) return null;

  const getIcon = (cat) => {
    if (cat === "Actionneur") return iconActionneur;
    if (cat === "Passerelle") return iconPasserelle;
    return iconCapteur;
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden mb-6">
      <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
        <MapPin size={14} className="text-orange-500" />
        <h3 className="text-sm font-semibold text-slate-800">Localisation des nœuds</h3>
        <span className="text-xs text-slate-400 ml-1">({geoNodes.length} positionnés)</span>
      </div>
      <div style={{ height: 350 }}>
        <MapContainer
          center={[geoNodes[0].lat, geoNodes[0].lng]}
          zoom={16}
          style={{ height: "100%", width: "100%" }}
          scrollWheelZoom={true}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
          />
          <FitBounds bounds={bounds} />
          {geoNodes.map((n, i) => (
            <Marker key={i} position={[n.lat, n.lng]} icon={getIcon(n.categorie)}>
              <Popup>
                <div className="text-xs leading-relaxed">
                  <p className="font-bold text-slate-800">{n.sn}</p>
                  <p className="text-slate-500">{n.categorie} — {n.type}</p>
                  <p className={n.etat === "En ligne" ? "text-green-600 font-medium" : "text-red-500 font-medium"}>
                    {n.etat}
                  </p>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
      {/* Légende */}
      <div className="px-5 py-2.5 border-t border-gray-100 flex items-center gap-5 text-xs text-slate-500">
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full bg-blue-500 border-2 border-white shadow-sm" /> Capteur
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full bg-green-500 border-2 border-white shadow-sm" /> Actionneur
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full bg-amber-500 border-2 border-white shadow-sm" /> Passerelle
        </span>
      </div>
    </div>
  );
}

export default function ChantierDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [chantier, setChantier] = useState(null);
  const [noeuds, setNoeuds] = useState([]);
  const [loading, setLoading] = useState(true);

  const { lastStatusChange, lastSensorData } = useSocket();

  useEffect(() => {
    const load = async () => {
      try {
        const [cRes, nRes] = await Promise.all([
          chantiersAPI.getOne(id),
          noeudsAPI.getAll({ chantier: id }),
        ]);
        setChantier(cRes.data?.data || cRes.data);
        setNoeuds(nRes.data?.data || []);
      } catch (err) {
        console.error("Erreur chargement chantier:", err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  // Mise à jour temps réel — statut
  useEffect(() => {
    if (!lastStatusChange) return;
    setNoeuds(prev => prev.map(n =>
      n.sn === lastStatusChange.sn
        ? { ...n, etat: lastStatusChange.etat, derniereMaj: new Date().toISOString() }
        : n
    ));
  }, [lastStatusChange]);

  // Mise à jour temps réel — données capteur
  useEffect(() => {
    if (!lastSensorData) return;
    setNoeuds(prev => prev.map(n =>
      n.sn === lastSensorData.sn
        ? {
            ...n,
            etat: 'En ligne',
            derniereValeur: lastSensorData.value ?? lastSensorData.valeur ?? n.derniereValeur,
            unite: lastSensorData.unit ?? lastSensorData.unite ?? n.unite,
            derniereMaj: new Date().toISOString(),
          }
        : n
    ));
  }, [lastSensorData]);

  // Modal override manuel
  const [overrideModal, setOverrideModal] = useState(null);

  const executeCommand = useCallback(async (nodeId, sn, activate, pauseAutoIds = []) => {
    try {
      await noeudsAPI.command(nodeId, {
        action: activate ? "Activer" : "Désactiver",
        pauseAutomatisations: pauseAutoIds
      });
      setNoeuds(prev => prev.map(n =>
        n._id === nodeId
          ? { ...n, derniereValeur: activate ? 1 : 0, derniereMaj: new Date().toISOString() }
          : n
      ));
    } catch (err) {
      console.error("Erreur commande actionneur:", err);
    }
  }, []);

  // Toggle actionneur ON/OFF — vérifie les autos avant d'exécuter
  const handleToggle = useCallback(async (nodeId, sn, activate) => {
    try {
      const projetId = chantier?.projet?._id || chantier?.projet;
      if (projetId) {
        const res = await automatisationsAPI.byActionneurs({ actionneurs: nodeId, projet: projetId });
        const autos = res.data?.data || [];
        if (autos.length > 0) {
          const noeud = noeuds.find(n => n._id === nodeId);
          setOverrideModal({ nodeId, sn, type: noeud?.type || '', activate, automations: autos });
          return;
        }
      }
      await executeCommand(nodeId, sn, activate);
    } catch (err) {
      await executeCommand(nodeId, sn, activate);
    }
  }, [chantier, noeuds, executeCommand]);

  if (loading) {
    return (
      <>
        <div className="flex items-center justify-center py-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600" />
          <span className="ml-3 text-sm text-slate-500">{t('common.loading')}</span>
        </div>
      </>
    );
  }

  if (!chantier) {
    return (
      <>
        <div className="text-center py-32 text-slate-400">Chantier introuvable</div>
      </>
    );
  }

  const capteurs = noeuds.filter(n => n.categorie === "Capteur");
  const actionneurs = noeuds.filter(n => n.categorie === "Actionneur");
  const passerelles = noeuds.filter(n => n.categorie === "Passerelle");
  const actifs = noeuds.filter(n => n.etat === "En ligne").length;

  const etatColor = {
    "En cours": "bg-blue-100 text-blue-700",
    "Terminé": "bg-green-100 text-green-700",
    "En attente": "bg-gray-100 text-gray-600",
    "En pause": "bg-amber-100 text-amber-700",
    "En attente de confirmation": "bg-purple-100 text-purple-700",
  };

  return (
    <>
      {/* Header */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-orange-50 rounded-2xl flex items-center justify-center">
              <Wrench size={22} className="text-orange-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-800">{chantier.nom}</h1>
              <p className="text-sm text-slate-400 mt-0.5">
                {chantier.projet?.nom || "—"} — {chantier.client?.nom || "—"}
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate("/chantiers")}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-slate-400"
          >
            <X size={20} />
          </button>
        </div>

        {/* Infos */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mt-5">
          <div className="bg-gray-50 rounded-xl px-4 py-3">
            <p className="text-xs text-slate-400">Dates</p>
            <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700 mt-0.5">
              <CalendarDays size={13} className="text-slate-400" />
              {formatDateLong(chantier.dateDebut)}
            </div>
            <p className="text-xs text-slate-400 mt-0.5">→ {formatDateLong(chantier.dateFin)}</p>
          </div>
          <div className="bg-gray-50 rounded-xl px-4 py-3">
            <p className="text-xs text-slate-400">État</p>
            <span className={`inline-block mt-1 px-2.5 py-0.5 rounded-lg text-xs font-semibold ${etatColor[chantier.etat] || "bg-gray-100 text-gray-600"}`}>
              {chantier.etat}
            </span>
          </div>
          <div className="bg-gray-50 rounded-xl px-4 py-3">
            <p className="text-xs text-slate-400">Chef de chantier</p>
            {(chantier.chefChantier || []).length > 0
              ? chantier.chefChantier.map((m, i) => (
                  <div key={i} className="flex items-center gap-1.5 mt-0.5">
                    <UserCircle size={13} className="text-blue-400" />
                    <p className="text-sm font-semibold text-slate-700">{m.nom || m}</p>
                  </div>
                ))
              : <p className="text-sm font-semibold text-slate-700 mt-0.5">—</p>
            }
          </div>
          <div className="bg-gray-50 rounded-xl px-4 py-3">
            <p className="text-xs text-slate-400">Techniciens</p>
            {(chantier.techniciens || []).length > 0
              ? chantier.techniciens.map((m, i) => (
                  <div key={i} className="flex items-center gap-1.5 mt-0.5">
                    <UserCircle size={13} className="text-purple-400" />
                    <p className="text-sm font-semibold text-slate-700">{m.nom || m}</p>
                  </div>
                ))
              : <p className="text-sm font-semibold text-slate-700 mt-0.5">—</p>
            }
          </div>
        </div>

        {chantier.commentaires && (
          <div className="mt-4 bg-gray-50 rounded-xl px-4 py-3">
            <p className="text-xs text-slate-400 mb-1">Commentaires</p>
            <p className="text-sm text-slate-600">{chantier.commentaires}</p>
          </div>
        )}
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 mb-6">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <div className="flex items-center gap-2 mb-1">
            <Cpu size={14} className="text-orange-500" />
            <span className="text-xs font-medium text-slate-400">Appareils installés</span>
          </div>
          <p className="text-3xl font-bold text-slate-800">{chantier.noeuds?.length || 0}</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle size={14} className="text-green-500" />
            <span className="text-xs font-medium text-slate-400">En ligne</span>
          </div>
          <p className="text-3xl font-bold text-slate-800">{actifs}</p>
        </div>
      </div>

      {/* Carte des nœuds */}
      <ChantierMap chantier={chantier} noeuds={noeuds} />

      {/* Nœuds du chantier */}
      <div className="mb-4">
        <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wide">
          Nœuds du chantier ({noeuds.length})
        </h2>
      </div>

      {noeuds.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-12 text-center">
          <Cpu size={32} className="text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-400">Aucun nœud installé sur ce chantier</p>
        </div>
      ) : (
        <div className="space-y-6">
          {capteurs.length > 0 && (
            <div>
              <span className="inline-block px-3 py-1 rounded-lg text-xs font-semibold bg-blue-100 text-blue-700 mb-3">
                Capteurs ({capteurs.length})
              </span>
              <div className="flex flex-wrap gap-4">
                {capteurs.map(n => <NodeCard key={n._id} noeud={n} />)}
              </div>
            </div>
          )}

          {actionneurs.length > 0 && (
            <div>
              <span className="inline-block px-3 py-1 rounded-lg text-xs font-semibold bg-green-100 text-green-700 mb-3">
                Actionneurs ({actionneurs.length})
              </span>
              <div className="flex flex-wrap gap-4">
                {actionneurs.map(n => <NodeCard key={n._id} noeud={n} onToggle={handleToggle} />)}
              </div>
            </div>
          )}

          {passerelles.length > 0 && (
            <div>
              <span className="inline-block px-3 py-1 rounded-lg text-xs font-semibold bg-amber-100 text-amber-700 mb-3">
                Connexion ({passerelles.length})
              </span>
              <div className="flex flex-wrap gap-4">
                {passerelles.map(n => <NodeCard key={n._id} noeud={n} />)}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal override manuel */}
      {overrideModal && (
        <ManualOverrideModal
          isOpen={!!overrideModal}
          onClose={() => setOverrideModal(null)}
          actionneur={{ sn: overrideModal.sn, type: overrideModal.type }}
          action={overrideModal.activate ? "Activer" : "Désactiver"}
          automations={overrideModal.automations}
          onConfirm={(pauseIds) => {
            executeCommand(overrideModal.nodeId, overrideModal.sn, overrideModal.activate, pauseIds);
            setOverrideModal(null);
          }}
        />
      )}
    </>
  );
}
